import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, tap } from 'rxjs';
import { environment } from '../../environments/environment';

/** KYC lifecycle. `NotSubmitted` means the partner has no KYC row yet. */
export type KycStatus = 'NotSubmitted' | 'Pending' | 'Approved' | 'Rejected';

/** Filter state for GetPartnersByKycState (`NotApproved` = NotSubmitted+Pending+Rejected). */
export type KycPartnerState =
  | 'NotApproved'
  | 'NotSubmitted'
  | 'Pending'
  | 'Rejected'
  | 'Approved';

/** Lightweight partner+KYC summary row from GetPartnersByKycState. */
export interface KycPartnerRow {
  partnerId: number;
  partnerName?: string | null;
  partnerCode?: string | null;
  contactPerson?: string | null;
  email?: string | null;
  phone?: string | null;
  kycStatus: KycStatus;
  submittedDate?: string | null;
}

/** Full KYC row (GetMyKyc / GetById / GetAll). */
export interface PartnerKyc {
  id?: number;
  partnerId: number;
  partnerName?: string | null;
  partnerCode?: string | null;
  kycStatus: KycStatus;
  rejectionReason?: string | null;

  fullName?: string | null;
  dob?: string | null;
  gender?: string | null;

  panNumber?: string | null;
  aadhaarNumber?: string | null;
  gstNumber?: string | null;

  email?: string | null;
  mobileNumber?: string | null;

  addressLine1?: string | null;
  addressLine2?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;

  bankName?: string | null;
  accountHolderName?: string | null;
  accountNumber?: string | null;
  ifscCode?: string | null;
  branchName?: string | null;

  panFilePath?: string | null;
  aadhaarFilePath?: string | null;
  gstFilePath?: string | null;
  cancelledChequePath?: string | null;

  isPANVerified?: boolean;
  isAadhaarVerified?: boolean;
  isGSTVerified?: boolean;
  isBankVerified?: boolean;
}

/** Body for Upsert (partner submit/update). */
export type PartnerKycUpsert = Omit<
  PartnerKyc,
  'id' | 'partnerName' | 'partnerCode' | 'kycStatus' | 'rejectionReason' |
  'isPANVerified' | 'isAadhaarVerified' | 'isGSTVerified' | 'isBankVerified'
>;

export interface KycApproveRequest {
  partnerId: number;
  isPANVerified: boolean;
  isAadhaarVerified: boolean;
  isGSTVerified: boolean;
  isBankVerified: boolean;
}

export interface KycRejectRequest {
  partnerId: number;
  rejectionReason: string;
}

/** Where the partner's OTP / password / messages are delivered. */
export interface MessageType {
  id?: number;
  partnerId: number;
  isWhatsApp: boolean;
  isSMS: boolean;
  isEmail: boolean;
}

/** Normalize a KYC status string from the API to our union (defaults to NotSubmitted). */
export function normalizeKycStatus(v: any): KycStatus {
  const s = String(v ?? '').toLowerCase();
  if (s === 'approved') return 'Approved';
  if (s === 'pending') return 'Pending';
  if (s === 'rejected') return 'Rejected';
  return 'NotSubmitted';
}

@Injectable({ providedIn: 'root' })
export class PartnerKycService {
  private http = inject(HttpClient);
  private readonly baseUrl = `${environment.apiUrl}/PartnerKyc`;

  /** Shared state: the logged-in partner's KYC status, used by the gate guard/interceptor. */
  readonly status = signal<KycStatus>('NotSubmitted');
  readonly loaded = signal(false);

  /** True once the logged-in partner's KYC is approved (unlocks the full app). */
  isApproved(): boolean {
    return this.status() === 'Approved';
  }

  /**
   * Title + text explaining why a non-approved partner is blocked, tailored to status.
   * Single source of truth shared by the kycGuard (navigation) and the sidebar (locked menu).
   */
  blockedAlert(): { title: string; text: string } {
    switch (this.status()) {
      case 'Pending':
        return {
          title: 'KYC under review',
          text: "Your KYC is pending approval. You can't access this until an admin approves it.",
        };
      case 'Rejected':
        return {
          title: 'KYC rejected',
          text: 'Your KYC was rejected. Please fix the details, re-upload your documents and re-submit before you can continue.',
        };
      default:
        return {
          title: 'KYC required',
          text: 'Please complete your KYC verification before using the rest of the app.',
        };
    }
  }

  // ===================== partner side =====================
  /** Load the logged-in partner's KYC into shared state; returns the row. */
  loadMyKyc(): Observable<PartnerKyc> {
    return this.getMyKyc().pipe(
      map((res: any) => {
        const data = res?.data ?? res ?? {};
        data.kycStatus = normalizeKycStatus(data.kycStatus);
        return data as PartnerKyc;
      }),
      tap((kyc) => {
        this.status.set(kyc.kycStatus);
        this.loaded.set(true);
      }),
    );
  }

  getMyKyc() {
    return this.http.get(`${this.baseUrl}/GetMyKyc`);
  }

  upsert(body: PartnerKycUpsert) {
    return this.http.post(`${this.baseUrl}/Upsert`, body);
  }

  // ===================== admin side =====================
  /** status: 'Pending' | 'Approved' | 'Rejected' | undefined (all). */
  getAll(status?: KycStatus) {
    const params = status ? { status } : undefined;
    return this.http.get(`${this.baseUrl}/GetAll`, params ? { params } : {});
  }

  getById(id: number) {
    return this.http.get(`${this.baseUrl}/GetById`, { params: { id } });
  }

  getByPartner(partnerId: number) {
    return this.http.get(`${this.baseUrl}/GetByPartner`, { params: { partnerId } });
  }

  /**
   * Partners filtered by KYC state. `state`:
   *  NotApproved (NotSubmitted+Pending+Rejected) | NotSubmitted | Pending | Rejected | Approved
   *  | undefined (all). Defaults to NotApproved server-side when omitted.
   */
  getPartnersByKycState(state?: KycPartnerState) {
    const params = state ? { state } : undefined;
    return this.http.get(`${this.baseUrl}/GetPartnersByKycState`, params ? { params } : {});
  }

  approve(body: KycApproveRequest) {
    return this.http.post(`${this.baseUrl}/Approve`, body);
  }

  reject(body: KycRejectRequest) {
    return this.http.post(`${this.baseUrl}/Reject`, body);
  }

  // ===================== message channels =====================
  getMessageType(partnerId: number) {
    return this.http.get(`${this.baseUrl}/GetMessageType`, { params: { partnerId } });
  }

  saveMessageType(body: MessageType) {
    return this.http.post(`${this.baseUrl}/SaveMessageType`, body);
  }

  /** Clear cached KYC state — call on logout so the next user loads their own. */
  reset() {
    this.status.set('NotSubmitted');
    this.loaded.set(false);
  }
}
